import { durationToUnitsSeconds, type EntryId, formatEntryId } from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import { type ActionDeps, actingHeaders, refreshEntries } from "./types";

export type AddPayload = {
	id: number;
	date: string;
	matter: string | null;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	timekeeperId?: string | null;
};

/** Map the form payload to the v2 wire shape. Empty strings become null. */
export function toCreateBody(entry: AddPayload) {
	return {
		date: entry.date,
		unitsSeconds: durationToUnitsSeconds(entry.duration),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskCode: entry.task || null,
		activityCode: entry.activity || null,
	};
}

export function addAction(
	entry: AddPayload,
	deps: ActionDeps,
	onSuccess?: (newEntryId: EntryId | null) => void,
	toast?: MutationAction["toast"],
): MutationAction {
	// Captured during execute() so the post-run onSuccess can forward it to
	// callers that want to chain follow-up work against the new row (e.g. the
	// /entries page's validated-export path).
	let newEntryId: EntryId | null = null;
	return createAction({
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entries",
				method: "POST",
				body: toCreateBody(entry),
				headers: actingHeaders(entry.timekeeperId),
			});
			if (resp.status === "ERROR")
				throw new Error(resp.error || "Request failed");
			if (resp.data?.id) {
				try {
					newEntryId = formatEntryId(resp.data.id);
				} catch {
					// Malformed id is non-fatal for the add itself — only the
					// chained export branch below needs the numeric id, and it
					// throws its own descriptive error.
				}
			}
		},
		rollback: null,
		toast: toast ?? {
			loading: "Adding entry…",
			success: "Entry added",
			error: "Failed to add entry",
		},
		onSuccess: () => {
			void refreshEntries(deps);
			onSuccess?.(newEntryId);
		},
		onError: () => {
			void refreshEntries(deps);
		},
	});
}
